import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  tasksApi,
  TASK_TYPES,
  type TaskListItemDto,
  type ListTasksParams,
  type TaskType,
} from "@/lib/tasks";
import { TaskTypeBadge } from "@/components/tasks/TaskTypeBadge";
import { TaskStatusBadge } from "@/components/tasks/TaskStatusBadge";
import { TaskPriorityIndicator } from "@/components/tasks/TaskPriorityIndicator";
import { CreateTaskDialog } from "@/components/tasks/CreateTaskDialog";
import { BulkTaskActionBar } from "@/components/tasks/BulkTaskActionBar";
import { EmployeePicker } from "@/components/employee-picker";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn } from "@/components/data-grid";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { usePermissions } from "@/hooks/use-permissions";
import { formatDate } from "@/lib/format";
import { toast } from "@/hooks/use-toast";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface TasksListViewProps {
  title: string;
  scope: "mine" | "all";
  backTo?: string;
  /** Reserved for T-2 (All Tasks). The assignee control is not rendered here. */
  showAssigneeFilter?: boolean;
  /** When true, renders an "Export CSV" button (All Tasks only). */
  showExport?: boolean;
}

// ---------------------------------------------------------------------------
// CSV helpers
// ---------------------------------------------------------------------------

/** Wraps a cell value in double-quotes if it contains `"`, `,`, or a newline,
 *  doubling any internal quotes per RFC 4180. */
function csvCell(v: string): string {
  if (/[",\n]/.test(v)) {
    return `"${v.replace(/"/g, '""')}"`;
  }
  return v;
}

// ---------------------------------------------------------------------------
// Filter state
//
// NOTE: due-date and priority chips are intentionally OUT of scope for T-1.
// The deliverable is a simple type + status + search filter set. Those chips
// land with the All-Tasks view (T-2) / detail filters.
// ---------------------------------------------------------------------------

type TypeChip = "All" | TaskType;
type StatusChip = "Open" | "Completed" | "Cancelled" | "All";

const STATUS_CHIPS: ReadonlyArray<{ id: StatusChip; label: string }> = [
  { id: "Open", label: "Open" },
  { id: "Completed", label: "Completed" },
  { id: "Cancelled", label: "Cancelled" },
  { id: "All", label: "All" },
];

const TYPE_OPTIONS: ReadonlyArray<TypeChip> = ["All", ...TASK_TYPES];

export function TasksListView({
  title,
  scope,
  backTo,
  showAssigneeFilter,
  showExport,
}: TasksListViewProps) {
  const { has } = usePermissions();
  const queryClient = useQueryClient();

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialType = ((): TypeChip => {
    const t = searchParams.get("type");
    return t && (TASK_TYPES as string[]).includes(t) ? (t as TypeChip) : "All";
  })();
  const initialStatus = ((): StatusChip => {
    const s = searchParams.get("status");
    return s === "Open" || s === "Completed" || s === "Cancelled" || s === "All"
      ? s
      : "Open";
  })();

  const [typeChip, setTypeChip] = useState<TypeChip>(initialType);
  const [statusChip, setStatusChip] = useState<StatusChip>(initialStatus);
  const [q, setQ] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  const [assigneeId, setAssigneeId] = useState<number | null>(null);
  const [assigneeLabel, setAssigneeLabel] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [exporting, setExporting] = useState(false);

  const clearSelection = () => setSelected(new Set());

  // Reset selection when the page or any filter changes — selected ids may no
  // longer be visible after the result set changes.
  useEffect(() => {
    setSelected(new Set());
  }, [pageIndex, typeChip, statusChip, q, assigneeId]);

  const canCreate = has("tasks:write");

  /** Returns the base filter params (without pagination) for the current filter state. */
  function buildParams(): Omit<ListTasksParams, "page" | "pageSize"> {
    return {
      type: typeChip === "All" ? undefined : typeChip,
      status: statusChip === "All" ? undefined : statusChip,
      q: q || undefined,
      scope,
      assigneeId: assigneeId ?? undefined,
    };
  }

  const params = useMemo<ListTasksParams>(
    () => ({
      ...buildParams(),
      page: pageIndex + 1,
      pageSize: 50,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [typeChip, statusChip, q, scope, pageIndex, assigneeId],
  );

  async function handleExport() {
    setExporting(true);
    try {
      // client-side export, capped at the API's 200-row max
      const exportParams: ListTasksParams = { ...buildParams(), page: 1, pageSize: 200 };
      const data = await tasksApi.list(exportParams);

      const header = "Id,Type,Status,Title,Priority,Assignee,Job,Due,Created";
      const rows = data.items.map((item) =>
        [
          csvCell(`T-${item.id}`),
          csvCell(item.taskType),
          csvCell(item.status),
          csvCell(item.title),
          csvCell(String(item.priority)),
          csvCell(item.assignedEmployeeName ?? ""),
          csvCell(item.jobReferenceNo ?? ""),
          csvCell(item.dueDateUtc ? formatDate(item.dueDateUtc) : ""),
          csvCell(formatDate(item.createdOnUtc)),
        ].join(","),
      );
      const csv = [header, ...rows].join("\n");

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "tasks.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      toast.error("Failed to export tasks");
    } finally {
      setExporting(false);
    }
  }

  const tasksQuery = useQuery({
    queryKey: ["tasks", params],
    queryFn: () => tasksApi.list(params),
  });

  const rows = tasksQuery.data?.items ?? [];

  const columns: DataGridColumn<TaskListItemDto>[] = [

    {
      id: "priority",
      header: "Priority",
      accessor: (r) => <TaskPriorityIndicator priority={r.priority} />,
    },
    {
      id: "type",
      header: "Type",
      accessor: (r) => <TaskTypeBadge taskType={r.taskType} />,
    },
    {
      id: "title",
      header: "Title",
      accessor: (r) => (
        <Link to={`/tasks/${r.id}`} className="underline text-primary">
          {r.title}
        </Link>
      ),
    },
    {
      id: "linked",
      header: "Linked",
      accessor: (r) =>
        r.jobReferenceNo
          ? `#${r.jobReferenceNo}${r.clientName ? ` · ${r.clientName}` : ""}`
          : "Ad-hoc",
    },
    {
      id: "due",
      header: "Due",
      accessor: (r) => (r.dueDateUtc ? formatDate(r.dueDateUtc) : "—"),
    },
    {
      id: "status",
      header: "Status",
      accessor: (r) => <TaskStatusBadge status={r.status} />,
    },
  ];

  return (
    <ListPage
      title={title}
      backTo={backTo}
      primaryAction={
        <div className="flex items-center gap-2">
          <Button variant="outline" asChild>
            <Link to="/tasks/summary">Summary</Link>
          </Button>
          {showExport && (
              <Button
                variant="outline"
                onClick={() => void handleExport()}
                disabled={exporting}
                title="Exports up to 200 rows of the current filter"
              >
                {exporting ? "Exporting…" : "Export CSV"}
              </Button>
            )}
            {canCreate && (
              <Button onClick={() => setCreateOpen(true)} className="gap-2">
                <Plus className="h-4 w-4" />
                New Task
              </Button>
            )}
          </div>
      }
      search={{
        value: q,
        onChange: (v) => {
          setQ(v);
          setPageIndex(0);
        },
        placeholder: "Search tasks…",
      }}
      filterChips={
        scope === "all"
          ? {
              options: STATUS_CHIPS,
              value: statusChip,
              onChange: (next) => {
                setStatusChip(next);
                setPageIndex(0);
              },
              "aria-label": "Filter tasks by status",
            }
          : undefined
      }
      toolbarExtra={
        <div className="flex flex-nowrap items-center gap-3">
          {scope === "mine" && (
            <div className="w-32">
              <Select
                value={statusChip}
                onValueChange={(v) => {
                  setStatusChip(v as StatusChip);
                  setPageIndex(0);
                }}
              >
                <SelectTrigger aria-label="Filter tasks by status" className="h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_CHIPS.map((opt) => (
                    <SelectItem key={opt.id} value={opt.id}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="w-48">
            <Select
              value={typeChip}
              onValueChange={(v) => {
                setTypeChip(v as TypeChip);
                setPageIndex(0);
              }}
            >
              <SelectTrigger aria-label="Filter tasks by type" className="h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TYPE_OPTIONS.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t === "All" ? "All types" : t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {showAssigneeFilter && (
            <div className="w-56">
              <span className="text-xs text-muted-foreground mb-1 block">
                Assignee
              </span>
              <EmployeePicker
                value={assigneeId}
                valueLabel={assigneeLabel}
                onChange={(id, emp) => {
                  setAssigneeId(id);
                  setAssigneeLabel(emp?.fullName ?? null);
                  setPageIndex(0);
                }}
              />
            </div>
          )}
          <BulkTaskActionBar
            selectedIds={[...selected]}
            onDone={clearSelection}
          />
        </div>
      }
    >
      <DataGrid
        rows={rows}
        columns={columns}
        rowKey={(r) => r.id}
        onRowClick={(r) => navigate(`/tasks/${r.id}`)}
        totalRows={tasksQuery.data?.totalCount ?? 0}
        pageIndex={pageIndex}
        pageSize={50}
        loading={tasksQuery.isLoading}
        emptyHeadline="No tasks"
        onPageChange={setPageIndex}
      />

      <CreateTaskDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        presetType={typeChip === "All" ? undefined : typeChip}
        onCreated={() => {
          void queryClient.invalidateQueries({ queryKey: ["tasks"] });
        }}
      />
    </ListPage>
  );
}
